import { create } from "zustand";
import { ZONES, works } from "./data";
import { detectQuality, hasCoarsePointer, prefersReducedMotion } from "./device";
import { workFocus } from "./layout";
import { parseHash } from "./route";
import { view } from "./viewState";

const THEME_KEY = "sp-theme";

function readTheme() {
  try {
    const t = window.localStorage.getItem(THEME_KEY);
    if (t === "dark" || t === "light") return t;
  } catch {
    /* storage can be blocked (private mode); fall through to default */
  }
  return "light";
}

function persistTheme(theme) {
  try {
    window.localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* ignore */
  }
}

const clampZone = (i) => Math.max(0, Math.min(ZONES.length - 1, i));
const quality = detectQuality();
const initial = typeof window !== "undefined" ? parseHash(window.location.hash) : { zone: 0, work: null };
const initialWorkIndex = initial.work ? works.findIndex((w) => w.slug === initial.work) : -1;

const cleared = { selectedWork: null, selectedSkill: null, focus: null, pan: 0 };

export const useStore = create((set, get) => ({
  // ---- environment -------------------------------------------------------
  theme: typeof window !== "undefined" ? readTheme() : "light",
  setTheme: (theme) => {
    persistTheme(theme);
    set({ theme });
  },
  toggleTheme: () => get().setTheme(get().theme === "dark" ? "light" : "dark"),
  quality,
  low: quality === "low",
  coarse: hasCoarsePointer(),
  reducedMotion: prefersReducedMotion(),
  setReducedMotion: (reducedMotion) => set({ reducedMotion }),
  setCoarse: (coarse) => set({ coarse }),

  // ---- navigation --------------------------------------------------------
  zoneIndex: initial.zone,
  goTo: (i) => set({ zoneIndex: clampZone(i), ...cleared }),
  step: (d) => set((s) => ({ zoneIndex: clampZone(s.zoneIndex + d), ...cleared })),
  // Apply a parsed URL without touching history (used by back/forward).
  applyRoute: ({ zone, work }) => {
    const idx = work ? works.findIndex((w) => w.slug === work) : -1;
    set({
      zoneIndex: zone,
      ...cleared,
      ...(idx >= 0 ? { selectedWork: work, focus: workFocus(idx) } : {}),
    });
  },

  // Horizontal panning inside a wide zone (touch drag / trackpad / mouse drag)
  pan: 0,
  pannable: false,
  setPan: (pan) => {
    const m = view.maxPan;
    set({ pan: Math.max(-m, Math.min(m, pan)) });
  },
  setPannable: (pannable) => set({ pannable }),
  dragging: false,
  setDragging: (dragging) => set({ dragging }),

  // ---- selection ---------------------------------------------------------
  focus: initialWorkIndex >= 0 ? workFocus(initialWorkIndex) : null,
  setFocus: (focus) => set({ focus }),
  selectedWork: initialWorkIndex >= 0 ? initial.work : null,
  selectWork: (slug) => {
    const i = works.findIndex((w) => w.slug === slug);
    if (i < 0) return;
    set({ selectedWork: slug, selectedSkill: null, focus: workFocus(i), pan: 0 });
  },
  // Jump from anywhere (e.g. the index panel) straight to a project.
  openWork: (slug) => {
    const i = works.findIndex((w) => w.slug === slug);
    if (i < 0) return;
    set({ zoneIndex: 1, selectedWork: slug, selectedSkill: null, focus: workFocus(i), pan: 0, indexOpen: false });
  },
  selectedSkill: null,
  selectSkill: (label) => set({ selectedSkill: label, selectedWork: null, focus: null }),
  openSkill: (label) =>
    set({ zoneIndex: 2, selectedSkill: label, selectedWork: null, focus: null, pan: 0, indexOpen: false }),
  clearSelection: () => set({ selectedWork: null, selectedSkill: null, focus: null }),

  // ---- overlays ----------------------------------------------------------
  pdf: null,
  setPdf: (pdf) => set({ pdf }),
  indexOpen: false,
  setIndexOpen: (indexOpen) => set({ indexOpen }),

  // ---- editable content / admin ------------------------------------------
  contentRev: 0, // bumps whenever site content changes so the UI re-renders with it
  bumpContent: () => set((s) => ({ contentRev: s.contentRev + 1 })),
  adminEnabled: false, // does the backend have the admin login switched on?
  setAdminEnabled: (adminEnabled) => set({ adminEnabled }),
  adminOpen: false,
  setAdminOpen: (adminOpen) => set({ adminOpen }),

  // ---- boot / health -----------------------------------------------------
  introDone: false,
  setIntroDone: () => set({ introDone: true }),
  sceneReady: false,
  sceneProgress: 0,
  setSceneProgress: (sceneProgress) => set({ sceneProgress }),
  setSceneReady: () => set({ sceneReady: true, sceneProgress: 100 }),
  // 'ok' | 'lost' | 'failed'
  gl: "ok",
  setGl: (gl) => set({ gl }),
  // true when WebGL/3D cannot be used and the 2D directory is shown instead
  fallback: false,
  setFallback: (fallback) => set({ fallback }),

  // ---- hover (3D objects) ------------------------------------------------
  hovered: false,
  hoverLabel: "",
  setHover: (hovered, hoverLabel = "") => set({ hovered, hoverLabel: hovered ? hoverLabel : "" }),
  setHovered: (hovered) => set({ hovered }),
}));
